<template>
  <div class="extended-properties">
    <ObjectComponent
      v-if="extensionsDb"
      :database-id="extensionsDb.id"
      :workspace-id="personalWorkspaceId"
      :id="resolvedExtensionObjectId"
      :mode="resolvedExtensionObjectId ? 'autosave' : 'lazy'"
      :embedded-mode="true"
      notes-display-mode="preview"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";

import type { TableDefaultExtensionsKind } from "@ctrl/controller-rpc";

import ObjectComponent from "@/components/graph/Object.vue";
import * as inject from "@/components/inject";
import { getDefaultDatabase } from "@/composables/useProfile";

export default defineComponent({
  name: "ExtendedProperties",
  components: {
    ObjectComponent,
  },
  props: {
    objectType: {
      type: String as PropType<TableDefaultExtensionsKind>,
      required: true,
    },
    objectId: {
      type: String,
      required: true,
    },
    extensionObjectId: {
      type: String as PropType<string | null | undefined>,
      default: null,
    },
    createExtensionObjectCallback: {
      type: Function as PropType<
        (() => Promise<string | undefined>) | undefined
      >,
      default: undefined,
    },
  },
  setup(props) {
    const profile = inject.profile();
    if (props.createExtensionObjectCallback) {
      inject.createObjectCallback(props.createExtensionObjectCallback);
    }

    // Live off the profile: schema edits (a property added to the type in
    // Settings) re-render the properties without a reload.
    const extensionsDb = computed(() =>
      getDefaultDatabase(profile.value, props.objectType)
    );
    const personalWorkspaceId = computed(
      () => profile.value.user.personal.workspaceId
    );

    const resolvedExtensionObjectId = computed<string | undefined>(() =>
      props.extensionObjectId === null ? undefined : props.extensionObjectId
    );

    return {
      resolvedExtensionObjectId,
      extensionsDb,
      personalWorkspaceId,
    };
  },
});
</script>

<style lang="scss" scoped>
.extended-properties {
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  align-items: flex-start;

  @include themed() {
    color: t($grey-70);
  }

  h3 {
    margin: 0;
    @extend %font-body-medium;
  }

  :deep(.object) {
    width: 100%;
  }

  :deep(.object .object-content) {
    margin: 0;
    width: 100%;
    max-width: none;
    align-items: flex-start;
    row-gap: 16px;
  }
}
</style>
